import { useNavigate, useSearchParams } from "@solidjs/router";
import {
  createEffect,
  createMemo,
  createResource,
  createSignal,
  For,
  onCleanup,
  onMount,
  Show,
} from "solid-js";
import type { BookingView, ParkingResponse, Vehicle } from "../../shared/api";
import { api, ApiFailure } from "../api";
import { CountdownRing } from "../components/CountdownRing";
import { Modal } from "../components/Modal";
import { SlideToConfirm } from "../components/SlideToConfirm";
import { BookingBand } from "../components/BookingBand";
import { MAP_TOP, MapTopRow } from "../components/MapTopRow";
import { MenuDrawer } from "../components/MenuDrawer";
import { ZoneSheet } from "../components/ZoneSheet";
import { type ZoneSheet as ZoneSheetData, zoneSheetAt } from "../../shared/zones";
import { ComingSoonToast } from "../components/ComingSoon";
import { Button } from "../components/Button";
import { OperatedBy } from "../components/OperatedBy";
import { LoadingPill, Spinner } from "../components/Spinner";
import { ScooterArt } from "../components/icons";
import { Pill } from "../components/Pill";
import { Sheet } from "../components/Sheet";
import { Stat } from "../components/Stat";
import { fleetArea } from "../map/fit";
import { createMapView, type MapSelection, type MapView } from "../map/view";
import {
  distanceMeters,
  freshPosition,
  geo,
  geoHint,
  walkLabel,
  watchPosition,
} from "../state/geo";
import { cancelBooking } from "../state/booking";
import { TYPE_LABEL } from "../pricingRows";
import { priceLabelFor } from "../state/pricing";
import { ItineraryBanner } from "../components/ItineraryBanner";
import { bannerFor } from "../itinerary";
import { useResumeOngoingTrip } from "../state/resume";
import { createRoutePath } from "../state/routePath";
import { tripPlan } from "../state/tripPlan";
import { nextStepFor, session } from "../state/session";

const POLL_MS = 10_000;

// Figma 03 / B2: buttons are 372 px at x 15, the sheet pads 20 px.
const BLEED = "!w-[calc(100%+10px)] -mx-[5px]";
// Framing keeps pins clear of the 02 panel (191 px on the Figma frame, plus its map buttons) and the
// menu button.
const PANEL_PADDING = { top: 80, right: 40, bottom: 240, left: 40 };
/** Pony vehicle photo (bike) or the scooter drawing, object-contain in the Figma box. */
function VehiclePhoto(props: { vehicle: Vehicle; width: number; height: number }) {
  // <Show>, not a ternary: a component body runs once, and the sheet stays mounted when the rider
  // selects another vehicle (a scooter's drawing stayed on the bike picked next).
  return (
    <Show
      when={props.vehicle.type === "scooter"}
      fallback={
        <img
          src="/img/ebike.webp"
          width={props.width}
          height={props.height}
          alt=""
          class="shrink-0 object-contain"
          style={{ width: `${props.width}px`, height: `${props.height}px` }}
        />
      }
    >
      <span class="shrink-0 flex items-center justify-center" style={{ width: `${props.width}px` }}>
        <ScooterArt size={Math.min(props.width, props.height)} />
      </span>
    </Show>
  );
}

const subtitleFor = (v: Vehicle) => `${TYPE_LABEL[v.type]}${v.type === "ebike" ? " 2 places" : ""}`;

/** Figma 02 Bike map, 02b Parking zone, 03 Vehicle selected. */
export function MapScreen() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  let container!: HTMLDivElement;
  const [view, setView] = createSignal<MapView>();
  const [selected, setSelected] = createSignal<MapSelection>();
  /** Figma K2–K5: the sheet of the zone tapped (selected().kind === "zone"). */
  const [zoneSheet, setZoneSheet] = createSignal<ZoneSheetData>();
  const select = (sel: MapSelection | undefined) => {
    const sheet =
      sel?.kind === "zone" && sel.at
        ? zoneSheetAt(sel.at, parking().zones ?? [], "ebike")
        : undefined;
    setZoneSheet(sheet);
    // A tap off the pins and off any zone just closes what is open.
    setSelected(sel?.kind === "zone" && !sheet ? undefined : sel);
  };
  const [menu, setMenu] = createSignal(false);
  // Feeds never reject: a failed poll keeps the last good data and raises the banner instead,
  // because reading an errored Solid resource throws inside effects and JSX.
  const [feedError, setFeedError] = createSignal(false);
  const keepLast =
    <T,>(path: string) =>
    async (_: unknown, info: { value: T | undefined }): Promise<T> => {
      try {
        const data = await api.get<T>(path);
        setFeedError(false);
        return data;
      } catch {
        setFeedError(true);
        if (info.value === undefined) throw new Error("unreachable: initialValue is set");
        return info.value;
      }
    };
  const [vehicles, { refetch }] = createResource(keepLast<Vehicle[]>("/vehicles"), {
    initialValue: [],
  });
  const [parking] = createResource(keepLast<ParkingResponse>("/parking"), {
    initialValue: { parking: [], forbidden: [], zones: [] } as ParkingResponse,
  });

  watchPosition();
  useResumeOngoingTrip();

  onMount(async () => {
    setView(await createMapView({ container, center: geo.position(), onSelect: select }));
  });

  // One-shot framing, never on later polls (that would undo the user's panning):
  // the rider's first real position wins; without it, the whole fleet; a deep link beats both.
  const area = createMemo(() => fleetArea(vehicles()));
  const deepLinkId = typeof params.vehicle === "string" ? params.vehicle : undefined;
  let framed = false;
  let flewToUser = false;
  createEffect(() => {
    const v = view();
    if (flewToUser || deepLinkId || !v || !geo.located()) return;
    flewToUser = true;
    v.flyTo(geo.position(), 15);
  });
  createEffect(() => {
    const v = view();
    const a = area();
    if (framed || !v || !a) return;
    framed = true;
    // Deep links carry the bookable Pony id only (never a position-derived map key).
    const target = deepLinkId ? vehicles().find((x) => x.vehicleId === deepLinkId) : undefined;
    if (target) {
      setSelected({ kind: "vehicle", id: target.id });
      v.flyTo(target, 16);
      // From the itinerary (C1b "Réserver le vélo" → C4): the booking modal straight away.
      if (params.book === "1") void askBooking(target);
    } else if (!flewToUser) v.fitBounds(a.bounds, PANEL_PADDING);
  });
  // A tap asks the device again (iOS prompts on a user gesture), then centres on the rider.
  const [locating, setLocating] = createSignal(false);
  const locate = async () => {
    const v = view();
    if (!v || locating()) return;
    if (!geo.located()) {
      setLocating(true);
      await freshPosition();
      setLocating(false);
    }
    const a = area();
    if (geo.located()) v.flyTo(geo.position(), 15);
    else if (a) v.fitBounds(a.bounds, PANEL_PADDING);
  };
  const locationHint = () => geoHint(geo.status(), navigator.userAgent);
  const timer = setInterval(() => {
    if (document.visibilityState === "visible") void refetch();
  }, POLL_MS);
  onCleanup(() => {
    clearInterval(timer);
    view()?.destroy();
  });

  createEffect(() => view()?.setVehicles(shownVehicles()));
  createEffect(() => view()?.setParking(parking()));
  createEffect(() => view()?.setSelected(selected()));
  createEffect(() => view()?.setUser(geo.located() ? geo.position() : undefined));

  const selectedVehicle = () =>
    selected()?.kind === "vehicle"
      ? shownVehicles().find((v) => v.id === selected()?.id)
      : undefined;
  const selectedParking = () =>
    selected()?.kind === "parking"
      ? parking().parking.find((p) => p.id === selected()?.id)
      : undefined;

  // The selected vehicle may vanish from the feed: taken by someone else, or (public GBFS without
  // _vehicle_id) its position-derived id changed because it moved a little. In the latter case follow it.
  let lastSelected: Vehicle | undefined;
  createEffect(() => {
    if (selected()?.kind !== "vehicle" || vehicles.loading) return;
    const current = selectedVehicle();
    if (current) {
      lastSelected = current;
      return;
    }
    const prev = lastSelected;
    const moved =
      prev && !prev.vehicleId
        ? vehicles()
            .filter((v) => !v.vehicleId && v.type === prev.type)
            .map((v) => ({ v, d: distanceMeters(prev, v) }))
            .filter((x) => x.d <= 15)
            .sort((a, b) => a.d - b.d)[0]?.v
        : undefined;
    setSelected(moved ? { kind: "vehicle", id: moved.id } : undefined);
  });

  // Wait for the session: during boot it is not loaded yet, and "unknown" is not "signed out".
  const [starting, setStarting] = createSignal<string | true>();
  const start = async (vehicleId?: string) => {
    setStarting(vehicleId ?? true);
    try {
      navigate(nextStepFor(await session.known(), vehicleId));
    } finally {
      setStarting(undefined);
    }
  };

  // ---- Booking (Figma B1–B3): 5 free minutes on TOMP one-stop --------------------------------
  const [booking, { refetch: refetchBooking, mutate: setBooking }] = createResource(
    () => session.signedIn() || undefined,
    async () => {
      try {
        return await api.get<BookingView>("/bookings/current");
      } catch (e) {
        if (e instanceof ApiFailure && (e.status === 404 || e.status === 401)) return null;
        return null;
      }
    },
  );
  // Countdown from the server's remainingSec (no client/server clock skew).
  const [now, setNow] = createSignal(Date.now());
  // Derived from the booking itself (a memo, not an effect) so the expiry check below never sees a
  // stale 0 right after a new booking arrives.
  const base = createMemo(() => ({ sec: booking()?.remainingSec ?? 0, at: Date.now() }));
  const ticker = setInterval(() => setNow(Date.now()), 1000);
  onCleanup(() => clearInterval(ticker));
  const remaining = () => Math.max(0, base().sec - Math.floor((now() - base().at) / 1000));
  const [expiredNotice, setExpiredNotice] = createSignal(false);
  createEffect(() => {
    if (booking() && remaining() === 0) {
      setBooking(null);
      setExpiredNotice(true);
      void refetchBooking();
    }
  });
  // Pony marks a booked bike as not available, so it leaves the feed: the booking carries its pin.
  const bookedVehicle = () => {
    const b = booking();
    return b ? (vehicles().find((v) => v.vehicleId === b.vehicleId) ?? b.vehicle) : undefined;
  };
  function shownVehicles(): Vehicle[] {
    const list = vehicles();
    const extra = bookedVehicle();
    return extra && !list.some((v) => v.id === extra.id) ? [...list, extra] : list;
  }
  // Figma B2: blue dotted walk from the rider to the booked bike, along the streets.
  const walkPath = createRoutePath(
    () => (booking() && geo.located() ? geo.position() : undefined),
    () => (booking() ? bookedVehicle() : undefined),
    "foot",
  );
  createEffect(() => view()?.setRoute(walkPath(), "blue"));
  // Figma 55:9383: the booked bike keeps its clock badge, selected or not.
  createEffect(() => view()?.setBooked(booking()?.vehicleId));
  // Show the booked vehicle once (cyan pin, B2 sheet).
  let focusedBooking: string | undefined;
  createEffect(() => {
    const b = booking();
    const v = bookedVehicle();
    const map = view();
    if (!b || !v || !map || focusedBooking === b.id) return;
    focusedBooking = b.id;
    setSelected({ kind: "vehicle", id: v.id });
    map.flyTo(v, 16);
  });

  // Booking length as Pony gives it (Expires of a booking; 5 min until one was made).
  const [terms] = createResource(() => api.get<{ minutes: number }>("/bookings/terms"), {
    initialValue: { minutes: 5 },
  });
  const bookingMinutes = () => (terms.state === "ready" ? terms().minutes : 5);
  const [bookTarget, setBookTarget] = createSignal<Vehicle>();
  const [cancelOpen, setCancelOpen] = createSignal(false);
  const [cancelError, setCancelError] = createSignal<string>();
  const [bookingError, setBookingError] = createSignal<string>();
  const askBooking = async (v: Vehicle) => {
    const step = nextStepFor(await session.known(), v.vehicleId);
    if (!step.startsWith("/scan")) return navigate(step); // sign in, activate or add a card first
    setBookingError(undefined);
    setBookTarget(v);
  };
  const [bookingBusy, setBookingBusy] = createSignal(false);
  const confirmBooking = async () => {
    const v = bookTarget();
    if (!v?.vehicleId) return;
    setBookingBusy(true);
    try {
      const b = await api.post<BookingView>("/bookings", {
        vehicleId: v.vehicleId,
        position: geo.position(),
      });
      setExpiredNotice(false);
      setBookTarget(undefined);
      setBooking(b);
    } catch (e) {
      const code = e instanceof ApiFailure ? e.code : "INTERNAL";
      if (code === "PHONE_VERIFICATION_REQUIRED")
        return navigate(`/phone?vehicle=${encodeURIComponent(v.vehicleId)}`);
      setBookingError(
        code === "ONGOING_BOOKING"
          ? "Vous avez déjà une réservation ou un trajet en cours."
          : code === "VEHICLE_NOT_AVAILABLE"
            ? "Ce véhicule n'est plus disponible."
            : code === "ACCOUNT_LINK_FAILED"
              ? "Votre compte n'a pas pu être relié au service vélo. Contactez le support."
              : "Réservation impossible pour le moment.",
      );
    } finally {
      setBookingBusy(false);
    }
  };
  const confirmCancel = async () => {
    const b = booking();
    if (!b) return;
    setBookingBusy(true);
    setCancelError(undefined);
    const r = await cancelBooking(b.id);
    setBookingBusy(false);
    if (!r.cancelled) return setCancelError(r.message); // Pony still holds the bike
    setCancelOpen(false);
    setBooking(null);
    setSelected(undefined);
  };

  // Prices page; back returns here with the bike selected again (deep link).
  const openPrices = (v?: Vehicle) => {
    // Plain replaceState: two router navigations in one tick merge into one (the replace wins).
    if (v?.vehicleId)
      history.replaceState(history.state, "", `/map?vehicle=${encodeURIComponent(v.vehicleId)}`);
    navigate("/tarifs");
  };

  return (
    <main class="screen-fixed bg-surface">
      <div class="absolute inset-0">
        <div ref={container} class="h-full w-full" />
      </div>

      {/* Figma map button Menu: 50 px violet, 11 px from the right, under the status bar. */}
      <button
        type="button"
        class="absolute right-[11px] h-[50px] w-[50px] rounded-full bg-violet flex items-center justify-center z-10"
        style={{ top: MAP_TOP }}
        onClick={() => setMenu(true)}
        aria-label="Menu"
      >
        <img src="/icons/menu.svg" width="26" height="26" alt="" />
      </button>
      <MenuDrawer open={menu()} onClose={() => setMenu(false)} />
      <ComingSoonToast />

      {/* Figma 02 (13:8014): 191 px sheet, 24 / 16 / 12 px rhythm, the home indicator below. */}
      <Show when={!selected()}>
        {/* Same rounded top and shadow as every bottom sheet (Figma draws this one flat). */}
        <section class="absolute inset-x-0 bottom-0 z-10 bg-white text-center rounded-t-[24px] shadow-[0_-2px_12px_rgba(0,0,0,0.16)]">
          <button
            type="button"
            class="absolute left-2 bottom-[calc(100%+22px)] h-12 w-12 rounded-full bg-white border border-black flex items-center justify-center"
            onClick={() => navigate("/")}
            aria-label="Retour"
          >
            <img src="/icons/back-black-26.svg" width="26" height="26" alt="" />
          </button>
          <button
            type="button"
            class="absolute right-[9px] bottom-[calc(100%+12px)] h-16 w-16 rounded-full bg-white border border-black flex items-center justify-center"
            onClick={() => void locate()}
            aria-label="Ma position"
            aria-busy={locating() || undefined}
          >
            <Show when={!locating()} fallback={<Spinner size={30} class="text-violet" />}>
              <img src="/icons/locate.svg" width="30" height="30" alt="" />
            </Show>
          </button>
          <div class="pt-6">
            <button
              type="button"
              class="mx-auto flex h-14 w-[280px] max-w-[calc(100%-30px)] items-center justify-center gap-[10px] rounded-[28px] bg-green text-white text-[17px] font-medium uppercase leading-[1.25] tracking-[0.34px] shadow-[0_2px_4px_rgba(0,0,0,0.18)] active:brightness-95"
              onClick={() => void start()}
              disabled={starting() === true}
              aria-busy={starting() === true || undefined}
            >
              <Show
                when={starting() !== true}
                fallback={
                  <>
                    <Spinner />
                    <span class="sr-only">Chargement</span>
                  </>
                }
              >
                <img src="/icons/qr-scan-white.svg" width="26" height="26" alt="" />
                Scanner un vélo
              </Show>
            </button>
          </div>
          <OperatedBy class="mt-4" />
          <a
            href="/tarifs"
            class="mt-1 flex h-[34px] w-full items-center justify-center text-[14px] leading-[1.25] tracking-[0.14px] text-link underline"
          >
            Tarifs et pass vélo
          </a>
          {/* Figma: 42 px under the link, 34 of them the iOS home indicator (the system's: only clear
              it). Its grey #f8f8fa strip is mock-up, not app UI. */}
          <div class="h-[calc(8px+max(8px,env(safe-area-inset-bottom)))]" />
        </section>
      </Show>

      {/* Figma B2: the booked vehicle. */}
      <Show
        when={
          selectedVehicle() && booking() && selectedVehicle()?.vehicleId === booking()?.vehicleId
        }
      >
        <Show when={selectedVehicle()}>
          {(v) => (
            <Sheet collapsible openKey={v().id}>
              <div class="-mt-[2px]">
                <BookingBand remainingSec={remaining()} />
              </div>
              {/* Collapsed, the booked sheet keeps the countdown band and the vehicle. */}
              <div data-sheet-peek class="mt-[14px] flex items-start gap-4">
                <VehiclePhoto vehicle={v()} width={92} height={78} />
                <div class="pt-[6px] leading-[1.25] min-w-0">
                  <div class="text-[22px] font-semibold tracking-[0.22px] text-strong">
                    {v().vehicleId}
                  </div>
                  <div class="mt-[2px] text-[13px] tracking-[0.13px] text-text">
                    {subtitleFor(v())}
                  </div>
                  <div class="mt-[6px] flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] font-medium tracking-[0.13px] text-text">
                    <span class="flex items-center gap-[6px]">
                      <img src="/icons/navigate-18.svg" width="18" height="18" alt="" />
                      {walkLabel(distanceMeters(geo.position(), v()))} à pied
                    </span>
                    <span class="flex items-center gap-[6px]">
                      <img src="/icons/battery-18.svg" width="18" height="18" alt="" />
                      {Math.round(v().rangeMeters / 1000)} km
                    </span>
                  </div>
                </div>
              </div>
              <Show when={priceLabelFor(v().pricingPlanId)}>
                {(label) => (
                  <p class="mt-[26px] text-center text-[12px] leading-[1.25] tracking-[0.12px] text-text">
                    Tarif : {label()}
                  </p>
                )}
              </Show>
              <Button
                class={`mt-[11px] ${BLEED}`}
                onClick={() => void start(v().vehicleId)}
                loading={starting() === v().vehicleId}
              >
                Scanner et démarrer
              </Button>
              <Button
                variant="outline"
                class={`mt-[10px] ${BLEED}`}
                onClick={() => setCancelOpen(true)}
              >
                Annuler la réservation
              </Button>
              <div class="h-6 short:h-0" />
            </Sheet>
          )}
        </Show>
      </Show>

      {/* Figma 03: an available vehicle. */}
      <Show
        when={
          selectedVehicle() && !(booking() && selectedVehicle()?.vehicleId === booking()?.vehicleId)
        }
      >
        <Show when={selectedVehicle()}>
          {(v) => (
            <Sheet onClose={() => setSelected(undefined)} collapsible openKey={v().id}>
              {/* Collapsed, the vehicle sheet keeps the vehicle (photo, status, id). */}
              <div data-sheet-peek class="flex items-start gap-3 pr-8">
                <VehiclePhoto vehicle={v()} width={104} height={90} />
                <div class="pt-1 leading-[1.25] min-w-0">
                  <Pill class="!px-0 w-[74px] justify-center">Disponible</Pill>
                  <div class="mt-[6px] text-[22px] font-semibold tracking-[0.22px] text-strong">
                    {v().vehicleId ?? TYPE_LABEL[v().type]}
                  </div>
                  <div class="mt-[2px] text-[13px] tracking-[0.13px] text-text">
                    {v().vehicleId ? subtitleFor(v()) : "Numéro indiqué sur le guidon"}
                  </div>
                </div>
              </div>
              <div class="mt-4 pt-4 border-t border-border flex justify-between gap-2">
                <Stat
                  ring="green"
                  icon={<img src="/icons/battery.svg" width="20" height="20" alt="" />}
                  value={`${Math.round(v().rangeMeters / 1000)} km`}
                />
                <Stat
                  ring="blue"
                  icon={<img src="/icons/navigate.svg" width="20" height="20" alt="" />}
                  value={walkLabel(distanceMeters(geo.position(), v()))}
                />
                <Show when={v().type === "ebike"}>
                  <Stat
                    ring="violet"
                    icon={<img src="/icons/person.svg" width="20" height="20" alt="" />}
                    value="2 places"
                  />
                </Show>
              </div>
              <Show when={priceLabelFor(v().pricingPlanId)}>
                {(label) => (
                  <button
                    type="button"
                    class="mt-[14px] h-[34px] w-full rounded-[8px] bg-surface2 flex items-center justify-center text-sm font-medium leading-[1.25] tracking-[0.14px] text-violet"
                    onClick={() => openPrices(v())}
                    aria-label={`Tarif : ${label()}. Voir les tarifs`}
                  >
                    Tarif : {label()}
                  </button>
                )}
              </Show>
              {/* Without a bookable id the rider types the number on the handlebar (scan screen). */}
              <Button
                class={`mt-[14px] ${BLEED}`}
                onClick={() => void start(v().vehicleId)}
                loading={starting() === v().vehicleId}
              >
                Scanner et démarrer
              </Button>
              <Show when={v().vehicleId && !booking()}>
                <Button
                  variant="outline"
                  class={`mt-2 ${BLEED}`}
                  onClick={() => void askBooking(v())}
                >
                  Réserver {bookingMinutes()} min gratuitement
                </Button>
              </Show>
            </Sheet>
          )}
        </Show>
      </Show>

      <Show when={bookTarget()}>
        {(v) => (
          <Modal
            label="Réserver ce véhicule"
            onClose={() => setBookTarget(undefined)}
            header={
              <CountdownRing
                remainingSec={bookingMinutes() * 60}
                totalSec={bookingMinutes() * 60}
                caption="min offertes"
              />
            }
          >
            <h2 class="modal-title">
              Réserver {v().type === "scooter" ? "cette trottinette" : "ce vélo"}
            </h2>
            <p class="modal-text mt-[10px] px-1">
              Vous pouvez réserver {v().type === "scooter" ? "cette trottinette" : "ce vélo"}{" "}
              gratuitement pendant {bookingMinutes()} minutes. Pendant ce temps,{" "}
              {v().type === "scooter" ? "elle" : "il"} ne sera plus disponible pour les autres
              utilisateurs.
            </p>
            <Show when={bookingError()}>
              {(m) => <p class="mt-2 text-sm text-red-700">{m()}</p>}
            </Show>
            <div class="mt-6">
              <SlideToConfirm
                label="Glissez pour réserver"
                onConfirm={confirmBooking}
                loading={bookingBusy()}
              />
            </div>
            <Button variant="outline" class="mt-3" onClick={() => setBookTarget(undefined)}>
              Annuler
            </Button>
          </Modal>
        )}
      </Show>

      <Show when={cancelOpen() && booking()}>
        <Modal
          label="Annuler la réservation"
          onClose={() => setCancelOpen(false)}
          header={
            <div class="relative">
              <CountdownRing
                remainingSec={remaining()}
                totalSec={booking()?.totalSec ?? bookingMinutes() * 60}
                caption="restantes"
              />
              {/* Figma red X badge (30 px at the ring's bottom-right); its hotspot dismisses. */}
              <button
                type="button"
                class="absolute left-[72px] top-[70px] h-[38px] w-[38px] flex items-center justify-center"
                onClick={() => setCancelOpen(false)}
                aria-label="Retour"
                tabindex="-1"
              >
                <span class="relative h-[30px] w-[30px]">
                  <img src="/icons/badge-circle-red.svg" width="30" height="30" alt="" />
                  <img
                    src="/icons/close-white-18.svg"
                    width="18"
                    height="18"
                    alt=""
                    class="absolute left-[6px] top-[6px]"
                  />
                </span>
              </button>
            </div>
          }
        >
          <h2 class="modal-title">Annuler la réservation</h2>
          <p class="modal-text mt-[10px] px-1">
            Votre vélo deviendra à nouveau disponible pour tous les utilisateurs.
          </p>
          <Show when={cancelError()}>
            {(m) => (
              <p class="mt-2 text-sm text-red-700" role="alert">
                {m()}
              </p>
            )}
          </Show>
          <div class="mt-6">
            <SlideToConfirm
              label="Glissez pour annuler"
              onConfirm={confirmCancel}
              loading={bookingBusy()}
            />
          </div>
          <Button variant="outline" class="mt-3" onClick={() => setCancelOpen(false)}>
            Retour
          </Button>
        </Modal>
      </Show>

      <Show when={expiredNotice()}>
        <div
          class="absolute top-20 inset-x-4 z-20 rounded bg-white px-3 py-2 text-sm text-strong shadow flex justify-between items-center"
          role="status"
        >
          <span>Votre réservation a expiré.</span>
          <button
            type="button"
            class="text-violet font-medium"
            onClick={() => setExpiredNotice(false)}
          >
            OK
          </button>
        </div>
      </Show>

      <Show when={selectedParking()}>
        {(p) => (
          <Sheet onClose={() => setSelected(undefined)}>
            <div class="flex items-start gap-3 pr-8">
              <span class="relative h-11 w-11 shrink-0">
                <img src="/icons/parking-circle-dark-blue.svg" width="44" height="44" alt="" />
                <img
                  src="/icons/parking-dark-blue-26.svg"
                  width="26"
                  height="26"
                  alt=""
                  class="absolute left-[9px] top-[9px]"
                />
              </span>
              <div class="leading-[1.25] min-w-0">
                <div class="text-[20px] font-semibold tracking-[0.2px] text-strong">{p().name}</div>
                <div class="mt-[3px] text-[13px] tracking-[0.13px] text-text">
                  {walkLabel(distanceMeters(geo.position(), p()))} à pied
                </div>
              </div>
            </div>
            <p class="mt-[22px] text-[14px] leading-[1.25] tracking-[0.14px] text-text">
              Terminez vos trajets dans une zone de stationnement. Garez le vélo à l'intérieur du
              périmètre, sans gêner le passage.
            </p>
            {/* Figma 75:10580: we are on the map already, "OK" just closes the sheet. */}
            <Button class={`mt-[51px] short:mt-6 ${BLEED}`} onClick={() => setSelected(undefined)}>
              OK
            </Button>
            <div class="h-10 short:h-0" />
          </Sheet>
        )}
      </Show>

      <Show when={selected()?.kind === "zone" && zoneSheet()}>
        {(z) => <ZoneSheet zone={z()} onClose={() => select(undefined)} />}
      </Show>

      {/* Figma 68:10148: notices on the menu button's row. */}
      <MapTopRow>
        <Show when={!view() || (vehicles.loading && vehicles().length === 0)}>
          <div class="flex justify-center">
            <LoadingPill label={view() ? "Chargement des vélos…" : "Chargement de la carte…"} />
          </div>
        </Show>
        {/* Figma C5: the followed itinerary's step while its bike is booked. */}
        <Show when={booking() && tripPlan.followedFor(booking()?.vehicleId)}>
          {(p) => <ItineraryBanner {...bannerFor(p(), "booked")} />}
        </Show>
        <Show when={!feedError() && locationHint()}>
          {(hint) => (
            <div class="rounded bg-white px-3 py-2 text-sm text-strong shadow" role="status">
              {hint()}
            </div>
          )}
        </Show>
        <Show when={feedError()}>
          <div class="rounded bg-white px-3 py-2 text-sm text-red-700 shadow">
            Impossible de charger les vélos.
          </div>
        </Show>
      </MapTopRow>
      <ul class="sr-only" aria-label="Vélos disponibles">
        <For each={vehicles()}>{(v) => <li>{v.id}</li>}</For>
      </ul>
    </main>
  );
}
